import { CdkListbox, CdkListboxModule } from '@angular/cdk/listbox';
import { CommonModule } from '@angular/common';
import { type AfterViewInit, DestroyRef, type WritableSignal, input } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import FuzzySearch from 'fuzzy-search';
import { distinctUntilChanged } from 'rxjs';
import { PalantirAccordionComponent } from '../../../../accordion/accordion.component';
import { PalantirIconComponent } from '../../../../icon/icon.component';
import { PalantirInputComponent } from '../../../../input/input.component';
import { PalantirRadioComponent } from '../../../../radio/radio.component';
import { PalantirTextComponent } from '../../../../text/text.component';
import type { PalantirFilterModalInstance } from '../../../filter-modal.instance';
import { PalantirFilterOption, PalantirFilterSelectInput } from '../../../filter-modal.type';
import { normalizeString, removeNormalizedFromOption } from '../../../filter-modal.utils';
import { PalantirFilterModalErrorDisplayComponent } from '../../shared/error-display/error-display.component';
import { PalantirFilterModalOptionComponent } from '../../shared/option/option.component';
import { filterModalInputLogger } from '../logger.instance';

const logger = filterModalInputLogger.extend('select');

@Component({
  selector: 'palantir-filter-modal-select-input-component',
  templateUrl: './radio.component.html',
  styleUrls: ['./radio.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PalantirAccordionComponent,
    PalantirInputComponent,
    ReactiveFormsModule,
    PalantirTextComponent,
    CdkListboxModule,
    PalantirRadioComponent,
    PalantirIconComponent,
    PalantirFilterModalErrorDisplayComponent,
    PalantirFilterModalOptionComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PalantirFilterModalSelectInputComponent implements AfterViewInit {
  @ViewChild('listBox', { static: true, read: CdkListbox })
  public listBox!: CdkListbox;

  public readonly definition = input.required<PalantirFilterSelectInput>();

  public readonly control = input.required<FormControl>();

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  public readonly modalInstance = input.required<PalantirFilterModalInstance<{}>>();

  public searchControl: FormControl = new FormControl<string>('');

  private searchEngine!: FuzzySearch<PalantirFilterOption>;

  public filteredOptions: WritableSignal<PalantirFilterOption[]> = signal([]);

  // eslint-disable-next-line unicorn/consistent-function-scoping
  public canShowFilter = computed(() => {
    const canShow = this.modalInstance().dependsOnFilterComputed(this.definition().dependsOn);
    logger.debug(`Visibility check for "${this.definition().label}":`, canShow);
    return canShow;
  });

  public constructor(private readonly destroyRef: DestroyRef) {}

  public ngAfterViewInit(): void {
    const definition = this.definition();
    this.filteredOptions.set(definition.options);

    const optionsWithNormalizedValues = definition.options.map((option) => ({
      ...option,
      normalized: normalizeString(option.label),
    }));

    this.searchEngine = new FuzzySearch(
      optionsWithNormalizedValues,
      ['normalized', 'label', 'value'],
      {
        caseSensitive: false,
        sort: true,
      },
    );

    this.searchControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.handleSearchChange(value));

    // Race condition between the control value and the list box options in html
    const control = this.control();
    if (control.value) {
      setTimeout(() => {
        this.listBox.selectValue(this.control().value);
      }, 100);
    }

    control.valueChanges.subscribe((value) => {
      this.listBox.writeValue(value);
    });
  }

  private handleSearchChange(value: string): void {
    logger.debug(`Search query changed for "${this.definition().label}":`, value);
    if (!value) {
      this.filteredOptions.set(this.definition().options);
      return;
    }

    const results = this.searchEngine.search(value).map(removeNormalizedFromOption);
    logger.debug(`Search results for "${this.definition().label}":`, results.length);
    this.filteredOptions.set(results);
  }

  public handleRadioChange(value: readonly any[]): void {
    logger.debug(`Radio selection changed for "${this.definition().label}":`, value[0]);
    this.control()?.setValue(value[0]);
    this.control()?.markAsDirty();
  }

  public clearOption() {
    this.control().setValue(null);
    this.control().markAsDirty();
  }

  /**
   * Previne a propagação do evento para o espaço não interagir com a checkbox
   */
  public stopSpacePropagation(event: Event): void {
    event.stopPropagation();
  }

  public addValueToNullOption(
    option: NonNullable<PalantirFilterSelectInput['nullOption']>,
  ): PalantirFilterOption {
    return {
      ...option,
      value: null,
    };
  }
}
